import React from 'react'
import { Button } from 'antd'
import { ArrowLeftOutlined, ExperimentOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import './venture.css'
import './ventureGuide.css'

const sections = [
    ['principles', '设计理念'],
    ['workflow', '验证流程'],
    ['canvas', '页面与字段'],
    ['evidence', '证据与信号'],
    ['decisions', '做出决策'],
    ['example', '完整示例'],
    ['getting-started', '开始使用'],
]

const stages = [
    ['Idea', '想法', '捕捉值得探索的问题。'],
    ['Experiment', '实验', '用有限投入验证假设。'],
    ['Evidence', '证据', '记录真实用户的行为。'],
    ['Decision', '决策', '根据证据决定下一步。'],
    ['Project', '正式项目', '把资源投入已验证的方向。'],
]

const metrics = [
    ['Exposure · 曝光', '有多少人看到了信息？', '落地页推广内容的曝光次数。'],
    ['Visit · 访问', '有多少人愿意进一步了解？', '来自目标用户渠道的有效访问。'],
    ['Signup · 注册', '有多少人留下了联系方式或注册？', '加入候补名单、创建账户。'],
    ['Activation · 激活', '有多少人真正体验到核心价值？', '完成一次插件辅助开发，而非仅打开页面。'],
    ['Payment · 付费', '有多少人用真实付款表达意愿？', '付费试用、订阅或购买。'],
    ['Retention · 留存', '有多少人过一段时间后继续使用？', '首次使用后一周内再次完成核心操作。'],
]

const decisions = [
    ['Kill', '停止并归档', '关键假设被证据否定，或继续投入已不值得。', '记录停止原因和收尾动作。想法变为 Killed（已停止），保留历史供查阅。'],
    ['Iterate', '继续迭代', '发现了问题，也找到了值得验证的调整方向。', '修改产品、用户定位或渠道，填写下一轮假设与实验。上一轮存档，新一轮的指标、证据和信号从头记录。'],
    ['Pause', '暂时冻结', '现在缺少时间、资源或合适的验证条件。', '写清重新评估的条件和下一步。想法变为 Paused（已暂停）；恢复实验会继续当前轮次，保留指标和证据。'],
    ['Scale', '正式立项', '已出现足以支持进一步投入的强信号。', '在 Testing（验证中）状态下确认 Strong（强信号），生成 Project（正式项目）记录。想法变为只读，保留立项依据和下一步。'],
]

function GuideSection({ id, number, title, children }) {
    return (
        <section className="vg-section" id={id} aria-labelledby={`${id}-title`}>
            <header className="vg-section-heading">
                <span>{number}</span><h2 id={`${id}-title`}>{title}</h2>
            </header>
            {children}
        </section>
    )
}

export default function VentureGuide() {
    const navigate = useNavigate()

    return (
        <main className="venture-studio venture-guide">
            <header className="vs-header">
                <div className="vs-brand">
                    <span className="vs-brand-icon"><ExperimentOutlined /></span>
                    <div>
                        <span className="vs-eyebrow">FIELD GUIDE · 实验室指南</span>
                        <h1>作战实验室 · 设计与使用说明</h1>
                        <p>让每一轮尝试，都帮助你做出更好的判断。</p>
                    </div>
                </div>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/venture')}>返回作战实验室</Button>
            </header>

            <div className="vg-intro">
                <span className="vs-eyebrow">THE CORE QUESTION · 核心问题</span>
                <h2>一个想法，是否值得成为项目？</h2>
                <p>想法可以很多，时间和注意力却有限。作战实验室帮助你把直觉写成假设，用小实验收集证据，再决定停止、迭代、暂停，还是正式立项。</p>
                <div className="vg-focus">Hypothesis · 假设 <span>→</span> Experiment · 实验 <span>→</span> Evidence · 证据 <span>→</span> Decision · 决策</div>
            </div>

            <div className="vg-layout">
                <nav className="vg-nav" aria-label="说明页目录">
                    <span className="vs-eyebrow">ON THIS PAGE · 本页目录</span>
                    {sections.map(([id, label], index) => <a key={id} href={`#${id}`}><span>0{index + 1}</span>{label}</a>)}
                </nav>

                <article className="vg-content">
                    <GuideSection id="principles" number="01" title="先验证，再投入">
                        <p>这个工具服务于立项前的判断。页面关注的是用户是否有需求、方案是否有效、渠道是否可行，以及用户是否愿意付费。任务完成度、排期和复杂财务管理属于后续执行阶段。</p>
                        <div className="vg-principles">
                            <div><h3>一次回答一个关键问题</h3><p>找出最可能让想法失败的假设，先验证它。明确问题，才能知道实验结束后学到了什么。</p></div>
                            <div><h3>投入有边界，判断有期限</h3><p>开始前写好成功标准、时间与金钱预算、决策日期。到期时回看证据，决定是否值得继续。</p></div>
                            <div><h3>控制同时进行的实验</h3><p>建议聚焦 2–3 个实验，当前页面上限为 3 个。每个想法同一时间只运行一个主要实验。</p></div>
                            <div><h3>保留每一次判断的依据</h3><p>决策会保存当时的假设、实验、指标和证据。及时停止缺乏需求的方向，也是一轮有价值的验证。</p></div>
                        </div>
                    </GuideSection>

                    <GuideSection id="workflow" number="02" title="从想法到正式项目">
                        <ol className="vg-stages">
                            {stages.map(([english, chinese, description]) => <li key={english}><strong>{english}<small>{chinese}</small></strong><p>{description}</p></li>)}
                        </ol>
                        <p>这个流程可以循环多轮。证据支持调整时，选择 Iterate（继续迭代），带着新的假设进入下一轮。只有当证据足以支撑进一步投入时，才进入正式项目阶段。</p>
                        <div className="vg-note"><strong>每轮实验结束时回答：</strong>我们原来相信什么？实际发生了什么？这些证据支持什么判断？下一步具体做什么？</div>
                    </GuideSection>

                    <GuideSection id="canvas" number="03" title="页面为什么这样设计">
                        <h3>左侧 Idea Pool · 想法池：先收集，再筛选</h3>
                        <p>快速记录名称、分类和一句话描述。用搜索、分类及状态筛选找到想法，用拖拽或箭头调整顺序。优先级表示你想先验证什么；信号强度表示现有证据支持到了什么程度。</p>
                        <details className="vg-details">
                            <summary>分类与状态分别是什么意思？</summary>
                            <dl className="vg-glossary">
                                <dt>Digital Entertainment · 数字娱乐</dt><dd>游戏、互动内容、数字娱乐体验。</dd>
                                <dt>Software · 软件产品</dt><dd>应用、插件、软件服务等。</dd>
                                <dt>Production Tools · 生产工具</dt><dd>帮助用户创作、制作内容或提高工作效率的工具。</dd>
                                <dt>Consumer · 消费产品</dt><dd>面向个人生活场景的产品或服务。</dd>
                                <dt>Knowledge · 知识产品</dt><dd>课程、内容、咨询与知识服务。</dd>
                                <dt>Other · 其他</dt><dd>暂时无法归入上述类别的方向。</dd>
                            </dl>
                            <p><strong>Inbox · 待整理</strong> → 新收集的想法；<strong>Candidate · 待验证</strong> → 准备探索的候选方向；<strong>Testing · 验证中</strong> → 已启动主要实验。</p>
                            <p><strong>Paused · 已暂停</strong>、<strong>Killed · 已停止</strong>、<strong>Scaled · 已立项</strong>由对应决策产生。已停止和已立项的想法只读。</p>
                        </details>
                        <h3>右侧 Idea Canvas · 想法画布：把问题讲清楚</h3>
                        <dl className="vg-glossary">
                            <dt>Problem · 问题</dt><dd>谁在什么情境下遇到了什么问题？现在如何应对？</dd>
                            <dt>User · 目标用户</dt><dd>具体是哪一类人？尽量写清群体和使用场景。</dd>
                            <dt>Solution · 解决方案</dt><dd>准备怎样解决问题，提供什么价值？</dd>
                            <dt>Distribution · 分发渠道</dt><dd>在哪里找到第一批真实目标用户？</dd>
                            <dt>Monetization · 商业模式</dt><dd>谁会为什么价值付费，准备如何收费？</dd>
                            <dt>Hypothesis · 核心假设</dt><dd>当前最需要验证的判断是什么？写成能被用户行为支持或否定的一句话。</dd>
                        </dl>
                        <h3>Current Experiment · 当前实验：先约定怎样算成功</h3>
                        <p>写清实验动作、成功标准、投入上限和截止日期。成功标准应与假设对应：如果要验证付费意愿，就需要观察真实付款或明确的购买行为。实验到期后，由你结合数据做出决策。</p>
                    </GuideSection>

                    <GuideSection id="evidence" number="04" title="记录能改变判断的证据">
                        <p>先确定目标用户、统计周期和每个指标的定义，再填写漏斗。尤其是 Activation（激活）和 Retention（留存），需要为你的产品明确“完成什么动作”以及“间隔多久”。</p>
                        <div className="vg-table-wrap" tabIndex={0} role="region" aria-label="核心漏斗指标说明，可横向滚动">
                            <table><caption>核心漏斗：从看到信息，到付费与持续使用</caption><thead><tr><th scope="col">指标</th><th scope="col">回答的问题</th><th scope="col">示例口径</th></tr></thead><tbody>
                                {metrics.map(([name, question, example]) => <tr key={name}><th scope="row">{name}</th><td>{question}</td><td>{example}</td></tr>)}
                            </tbody></table>
                        </div>
                        <p>页面显示的百分比是相对上一阶段的转化率。使用同一实验周期和可比较的统计口径，才能看出用户在哪一步流失。</p>
                        <div className="vg-evidence-types">
                            <div><h3>Positive · 正向证据</h3><p>支持假设的行为，例如目标用户主动询价、实际付款或持续使用。</p></div>
                            <div><h3>Negative · 负向证据</h3><p>削弱假设的行为，例如注册后不使用、体验后明确拒绝付费。</p></div>
                            <div><h3>Neutral · 中性证据</h3><p>目前不足以支持或否定假设的信息，例如尚未确认是否属于目标人群的反馈。</p></div>
                        </div>
                        <h3>Signal · 信号强度，是你对证据的评估</h3>
                        <p>None（暂无信号）、Weak（弱信号）、Moderate（中等信号）、Strong（强信号）由你手动选择，页面没有统一的自动评分公式。可以结合用户匹配程度、实际投入意愿、付费、留存以及行为是否重复出现来判断。</p>
                        <p>曝光和点赞说明有人注意到你，后续行为才能帮助判断需求。样本很少时，记录它的局限；Strong（强信号）表示值得继续投入验证，并不保证项目一定成功。</p>
                        <div className="vg-note">顶部 Strong Signals（强信号）统计已标记 Strong 且未暂停或停止的想法。Dashboard（概览）帮助分配注意力，实际决策仍要回到具体实验与证据。</div>
                    </GuideSection>

                    <GuideSection id="decisions" number="05" title="四种决策，四种下一步">
                        <p>每次决策都必须写下<strong>原因和下一步行动</strong>。原因引用这轮观察到的证据，下一步写成可以执行的具体动作。</p>
                        <div className="vg-decisions">
                            {decisions.map(([type, label, when, result]) => <div className={`vg-decision vg-decision-${type.toLowerCase()}`} key={type}><h3>{type} · {label}</h3><p><strong>何时考虑：</strong>{when}</p><p><strong>选择之后：</strong>{result}</p></div>)}
                        </div>
                        <p>Iterate 和 Scale 用于已经启动的实验。暂停、停止或立项会释放活跃实验名额；Iterate 继续占用原来的一个名额。</p>
                        <div className="vg-note"><strong>立项意味着把判断转为投入。</strong>当前 Project 是保存在实验室内的立项记录，包含来源想法、决策、时间和下一步行动。后续项目执行可以在你使用的项目工具中继续安排。</div>
                    </GuideSection>

                    <GuideSection id="example" number="06" title="示例：验证 Godot AI 插件的付费意愿">
                        <p className="vg-example-label">以下内容为演示数据，用于说明思考方式，不是通用成功门槛。</p>
                        <div className="vg-example">
                            <dl className="vg-glossary">
                                <dt>想法与假设</dt><dd>为独立游戏开发者提供 Godot AI 辅助插件。假设：这类开发者愿意为减少重复开发工作付费。</dd>
                                <dt>实验</dt><dd>制作 Landing Page（落地页）和简短演示，在相关 Reddit 社区分发。</dd>
                                <dt>成功标准</dt><dd>14 天内获得 200 次精准访问、20 个注册、2 个真实付费用户。</dd>
                                <dt>预算与期限</dt><dd>最多投入 10 小时，实验开始前选定第 14 天作为决策日期。</dd>
                            </dl>
                            <h3>第 14 天，实际发生了什么？</h3>
                            <p>获得 210 次精准访问、22 个注册，只有 2 人完成核心使用，0 人付费。3 人主动询问价格，但进一步访谈发现，大多数人没有顺利完成安装。</p>
                            <p><strong>判断：</strong>访问和注册达标，付费目标未达成。询价是正向证据，低激活和零付费是负向证据；还不足以确认付费假设。</p>
                            <p><strong>决策：</strong>Iterate（继续迭代）。原因是安装障碍可能阻止用户体验价值，需要进一步验证。下一步是简化安装，并邀请已有注册用户完成一次实际开发操作。</p>
                            <p><strong>下一轮：</strong>假设“目标用户顺利体验核心功能后，愿意为节省开发时间付费”。重新设定成功标准、预算和截止日期，保存旧轮次，开始下一轮实验。</p>
                        </div>
                    </GuideSection>

                    <GuideSection id="getting-started" number="07" title="开始你的第一轮验证">
                        <ol className="vg-checklist">
                            <li>添加一个想法，用一句话写清它面向谁、解决什么问题。</li>
                            <li>填写想法画布，选出最重要的一条待验证假设。</li>
                            <li>设计一个小实验，提前填写成功标准、预算和截止日期，再点击「开始实验」。</li>
                            <li>在同一轮中持续记录漏斗数据和真实反馈，点击「保存修改」保存编辑；证据通过「记录证据」提交。</li>
                            <li>到期或预算用尽时回看证据，选择一种决策，写清原因与下一步。</li>
                        </ol>
                        <details className="vg-details">
                            <summary>使用时还有哪些规则？</summary>
                            <ul>
                                <li>已有 3 个活跃实验时，先暂停、停止或完成一个，再开始新的验证。</li>
                                <li>开始实验、记录证据和提交决策时，会先保存当前想法的编辑。尚未提交的证据需先记录或清空，再做决策。</li>
                                <li>每次决策的快照可在 Decision Log（决策记录）展开查看。Iterate 会重置新一轮数据，Pause 恢复时保留当前轮次。</li>
                                <li>已停止或立项的想法只读。如未来出现新的验证条件，可以新建想法，并在描述中注明之前的结论。</li>
                                <li>「设计与使用说明」在新标签页打开，阅读时可以保留原页面中的编辑。</li>
                            </ul>
                        </details>
                        <div className="vg-start"><p>从一个小实验开始，让下一次投入更有依据。</p><Button type="primary" onClick={() => navigate('/venture')}>进入作战实验室</Button></div>
                    </GuideSection>
                </article>
            </div>
        </main>
    )
}
